import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronRight, ExternalLink, Link2, Pencil, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Field';
import { useToast } from '@/components/ui/ToastProvider';
import { ModuleActivity } from '@/components/ui/ModuleActivity';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { EmptyState, Skeleton } from '@/components/ui/Feedback';
import { StatusPill } from '@/components/ui/StatusPill';
import { Modal } from '@/components/ui/Modal';
import { ArchiveAction } from '@/components/ui/ArchiveAction';
import { ArchiveFilter } from '@/components/ui/ArchiveFilter';
import { SopEditor } from './SopEditor';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { BoardMenu } from '@/features/dashboards/BoardMenu';
import { setMinimised, useMinimised } from '@/features/dashboards/minimised';
import { formatDate } from '@/lib/format';

interface SopStep {
  tab: string | null;
  section: string | null;
  label: string;
  description: string | null;
}

export interface Sop {
  id: string;
  key: string;
  title: string;
  /** Archived procedures are listed only when asked for. */
  archived?: boolean;
  owner_role_key: string | null;
  /** The nav module whose "?" opens it. */
  module: string | null;
  /** The live document — usually a Google Doc — that the steps summarise. */
  document_url: string | null;
  owner: string | null;
  version: string;
  reviewed_on: string | null;
  review_due_on: string | null;
  overdue: boolean;
  step_count: number;
  steps: SopStep[];
}

export interface SopsResponse {
  sops: Sop[];
  overdue: string[];
  can_edit: boolean;
  roles: { key: string; label: string }[];
}

/**
 * Company → SOPs.
 *
 * Round 3 item 11 and comment #36: a central register with an owner and a review
 * date. The register earns its keep by answering the one question a folder of
 * procedures cannot — which of these has nobody looked at lately.
 */
export function useSops(showArchived = false) {
  return useQuery({
    queryKey: ['company', 'sops', showArchived],
    queryFn: () =>
      api.get<SopsResponse>('/company/sops', { query: { archived: showArchived ? 'show' : undefined } }),
  });
}

/**
 * One SOP shown where the work happens — the tender SOP beside the tender
 * checklist, and so on (client round 4: an SOP filed away is never followed).
 */
export function ModuleSop({ sopKey }: { sopKey: string }) {
  const sops = useSops();
  const [open, setOpen] = useState(false);
  const sop = sops.data?.sops.find((entry) => entry.key === sopKey);

  if (!sop) return null;

  return <SopCard sop={sop} open={open} onToggle={() => setOpen((value) => !value)} lead="Follow the SOP" />;
}

/** Where an SOP shows and the live document it points at — set by an administrator. */
function SopLinkEditor({ sop }: { sop: Sop }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [url, setUrl] = useState(sop.document_url ?? '');
  const save = useMutation({
    mutationFn: () => api.put<SopsResponse>(`/company/sops/${sop.id}`, { module: sop.module, document_url: url.trim() || null }),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['company', 'sops'], fresh);
      toast.success('Live document link saved');
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : 'The link could not be saved.'),
  });

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-line bg-cloud/40 px-5 py-3">
      <Link2 aria-hidden className="h-4 w-4 shrink-0 text-muted" />
      <Input
        aria-label={`Live document link for ${sop.title}`}
        placeholder="Paste the Google Drive link to the live document"
        value={url}
        onChange={(event) => setUrl(event.target.value)}
        className="min-w-[240px] flex-1"
      />
      <Button size="sm" variant="outline" loading={save.isPending} disabled={url.trim() === (sop.document_url ?? '')} onClick={() => save.mutate()}>
        Save link
      </Button>
    </div>
  );
}

export function SopCard({
  sop,
  open,
  onToggle,
  lead,
  canEdit = false,
  onEdit,
  onArchive,
}: {
  sop: Sop;
  open: boolean;
  onToggle: () => void;
  lead?: string;
  canEdit?: boolean;
  onEdit?: (sop: Sop) => void;
  onArchive?: (sop: Sop) => void;
}) {
  return (
    <Card padded={false}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full flex-wrap items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-cloud/50"
      >
        <span className="min-w-0">
          <span className="flex flex-wrap items-center gap-2">
            <ChevronRight aria-hidden className={cn('h-4 w-4 shrink-0 text-muted transition-transform', open && 'rotate-90')} />
            {lead && <span className="text-helper font-semibold uppercase tracking-[0.06em] text-muted">{lead}</span>}
            <span className="text-card-title text-ink">{sop.title}</span>
            <span className="text-helper text-muted">{sop.version}</span>
          </span>
          <span className="mt-0.5 block pl-6 text-helper text-muted">
            {sop.step_count} steps
            {sop.owner ? ` · owned by ${sop.owner}` : ' · unassigned'}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {sop.archived === true && <StatusPill tone="neutral">Archived</StatusPill>}
          {sop.review_due_on && (
            <StatusPill tone={sop.overdue ? 'danger' : 'neutral'}>
              {sop.overdue ? 'Review overdue' : `Review ${formatDate(sop.review_due_on)}`}
            </StatusPill>
          )}
        </span>
      </button>

      {canEdit && onEdit && (
        <div className="flex items-center gap-1 border-t border-line px-5 py-2" onClick={(event) => event.stopPropagation()}>
          <Button size="sm" variant="ghost" leadingIcon={<Pencil />} onClick={() => onEdit(sop)}>
            Edit
          </Button>
          <ArchiveAction
            archived={sop.archived === true}
            name={sop.title}
            path={`/company/sops/${sop.id}`}
            noun="SOP"
            invalidate={['company', 'sops']}
            onArchive={() => onArchive?.(sop)}
          />
        </div>
      )}

      {sop.document_url && (
        <a
          href={sop.document_url}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 border-t border-line px-5 py-2.5 text-helper font-semibold text-digital hover:underline"
        >
          <ExternalLink aria-hidden className="h-3.5 w-3.5" />
          Open the live document
        </a>
      )}
      {open && canEdit && <SopLinkEditor sop={sop} />}

      {open && (
        <ol className="border-t border-line">
          {sop.steps.map((step, index) => {
            const previous = sop.steps[index - 1];
            /*
             * The workbook's own two levels, kept.
             *
             * These came from spreadsheets with named sheets, and the Tendering
             * one is 137 steps across six of them. Grouping by section alone
             * ran the checklist straight into the folder structure with nothing
             * between them.
             */
            const newTab = index === 0 || previous?.tab !== step.tab;
            const newSection = newTab || previous?.section !== step.section;

            return (
              <li key={`${step.label}-${index}`}>
                {newTab && step.tab && (
                  <p className="border-b border-line bg-pale-blue/60 px-5 py-2.5 text-2xs font-bold uppercase tracking-[0.06em] text-ink">
                    {step.tab}
                  </p>
                )}
                {newSection && step.section && (
                  <p className="border-b border-line bg-cloud/70 px-5 py-2 text-2xs font-semibold uppercase tracking-[0.06em] text-muted">
                    {step.section}
                  </p>
                )}
                <div className="border-b border-line px-5 py-3 last:border-0">
                  <p className="text-sm font-semibold text-body">{step.label}</p>
                  {step.description && <p className="mt-0.5 text-helper leading-relaxed text-muted">{step.description}</p>}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}

/** A register row, open or shut, remembered. */
function FoldableSopCard({
  sop,
  canEdit,
  onEdit,
  onArchive,
}: {
  sop: Sop;
  canEdit?: boolean;
  onEdit: (sop: Sop) => void;
  onArchive: (sop: Sop) => void;
}) {
  const [folded, toggle] = useMinimised(`sop:${sop.id}`, true);

  return (
    <SopCard sop={sop} open={!folded} onToggle={toggle} canEdit={canEdit} onEdit={onEdit} onArchive={onArchive} />
  );
}

export function SopsPage() {
  /** The SOP being written or rewritten; `null` while nothing is. */
  const [editing, setEditing] = useState<Sop | null | undefined>(undefined);
  const [archiving, setArchiving] = useState<Sop | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const sops = useSops(showArchived);
  const queryClient = useQueryClient();
  const toast = useToast();

  const archive = useMutation({
    mutationFn: (sop: Sop) => api.delete<SopsResponse>(`/company/sops/${sop.id}`),
    onSuccess: (fresh, sop) => {
      queryClient.setQueryData(['company', 'sops', showArchived], fresh);
      queryClient.invalidateQueries({ queryKey: ['company', 'sops'] });
      toast.success(`${sop.title} archived`);
      setArchiving(null);
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : 'That SOP could not be archived.'),
  });

  /*
   * Which procedures are open is remembered, and more than one can be.
   *
   * It used to be one at a time in a `useState`, which meant opening the
   * second one shut the first — no good when you are reading the tendering
   * steps alongside the estimating ones — and meant Expand all had nothing to
   * act on. It is the same store the widget boards use, declared
   * `closedByDefault` because a register that opens 137 steps on arrival is
   * not a register.
   */
  const foldIds = (sops.data?.sops ?? []).map((sop) => `sop:${sop.id}`);

  // Opened from the top bar's search: expand the procedure that was chosen.
  const wanted = new URLSearchParams(window.location.search).get('search');
  useEffect(() => {
    if (!wanted || !sops.data) return;
    const match = sops.data.sops.find((sop) => sop.title.toLowerCase() === wanted.toLowerCase());
    if (match) setMinimised([`sop:${match.id}`], false, true);
  }, [wanted, sops.data]);

  return (
    <PageContainer className="space-y-5">
      <PageHeader
        title="SOP Register"
        description="Every standard operating procedure, who owns it and when it is due for review. Each one also shows in its own module — the Tendering SOP beside the tender checklist."
        actions={
          sops.data?.can_edit ? (
            <Button leadingIcon={<Plus />} onClick={() => setEditing(null)}>
              New SOP
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <ArchiveFilter
          value={showArchived ? 'show' : 'hide'}
          onChange={(mode) => setShowArchived(mode !== 'hide')}
          noun="SOPs"
        />
        <BoardMenu ids={foldIds} closedByDefault />
      </div>

      {sops.isPending ? (
        <Skeleton className="h-[300px] rounded-xl" />
      ) : (sops.data?.sops ?? []).length === 0 ? (
        <Card>
          <EmptyState title="No SOPs recorded" description="Procedures appear here." />
        </Card>
      ) : (
        <div className="space-y-4">
          {(sops.data?.overdue.length ?? 0) > 0 && (
            <Card className="border-l-4 border-l-danger">
              <p className="flex items-start gap-2 text-sm text-body">
                <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-danger-600" />
                <span>
                  <span className="font-semibold">Overdue for review:</span> {sops.data?.overdue.join(', ')}. A procedure
                  nobody has looked at in years is a compliance problem, and that is invisible from inside the document.
                </span>
              </p>
            </Card>
          )}

          {(sops.data?.sops ?? []).map((sop) => (
            <FoldableSopCard
              key={sop.id}
              sop={sop}
              canEdit={sops.data?.can_edit}
              onEdit={setEditing}
              onArchive={setArchiving}
            />
          ))}
        </div>
      )}

      {/* What changed on this screen, and who changed it. */}
      <ModuleActivity module="company-sops" />

      {editing !== undefined && (
        <SopEditor sop={editing} roles={sops.data?.roles ?? []} onClose={() => setEditing(undefined)} />
      )}

      <Modal
        open={archiving !== null}
        onClose={() => setArchiving(null)}
        title={`Archive ${archiving?.title ?? ''}?`}
        description="It leaves the register and stops showing in its module. A procedure that was in force is how you explain what somebody was following at the time, so it is kept and can be restored."
        footer={
          <>
            <Button variant="outline" onClick={() => setArchiving(null)} disabled={archive.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              loading={archive.isPending}
              onClick={() => archiving && archive.mutate(archiving)}
            >
              Archive SOP
            </Button>
          </>
        }
      />
    </PageContainer>
  );
}
